import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { PedidosNuevosAPI } from '../../services/pedidosNuevosService';
import {
  PedidoListaItem,
  FiltrosPedidosNuevos,
  RespuestaListadoPedidos,
} from '../../types/pedidosNuevos';
import TarjetaPedido from '../TarjetaPedido';
import ConfirmModal from '../modals/ConfirmModal';
import RechazarPedidoModal from '../modals/RechazarPedidoModal';
import ToggleVencidos from './ToggleVencidos';
import { useNavigate } from 'react-router-dom';
import { getHoyLocal } from '../../utils/fechaActual';
import {
  Search,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  Clock,
  Plus,
} from 'lucide-react';

const getAyerLocal = (): string => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Tab para admin/chef: ver pedidos solicitados (pendientes de aprobar)
 */
const PedidosEntrantesTab: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pedidos, setPedidos] = useState<PedidoListaItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Paginación y filtros
  const [paginaActual, setPaginaActual] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [totalPedidos, setTotalPedidos] = useState(0);
  const [busqueda, setBusqueda] = useState('');
  // Default vigentes (fecha_entrega >= hoy). Toggle a vencidos.
  const [modoVencidos, setModoVencidos] = useState(false);

  // Estados para modales
  const [modalConfirm, setModalConfirm] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning' as 'warning' | 'danger' | 'info',
    onConfirm: () => {},
  });
  const [modalMensaje, setModalMensaje] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info' as 'warning' | 'danger' | 'info',
  });
  const [modalRechazar, setModalRechazar] = useState({
    isOpen: false,
    pedido: null as PedidoListaItem | null,
  });

  const cargarPedidos = async () => {
    setLoading(true);
    setError(null);

    try {
      const filtros: FiltrosPedidosNuevos = {
        estado: 'solicitado', // Solo pedidos solicitados
        busqueda: busqueda || undefined,
        page: paginaActual,
        limit: 10,
      };
      if (modoVencidos) {
        filtros.fechaHasta = getAyerLocal();
      } else {
        filtros.fechaDesde = getHoyLocal();
      }

      const respuesta: RespuestaListadoPedidos =
        await PedidosNuevosAPI.listarPedidos(filtros);

      setPedidos(respuesta.data);
      setTotalPaginas(respuesta.pagination.totalPages);
      setTotalPedidos(respuesta.pagination.total);
    } catch (err: any) {
      console.error('Error al cargar pedidos:', err);
      setError(err.message || 'Error al cargar los pedidos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarPedidos();
  }, [paginaActual, modoVencidos]);

  const handleBuscar = () => {
    setPaginaActual(1);
    cargarPedidos();
  };

  const handleVerDetalle = (pedido: PedidoListaItem) => {
    navigate(`/pedidos/${pedido.id}`);
  };

  const handleAprobar = async (pedido: PedidoListaItem) => {
    setModalConfirm({
      isOpen: true,
      title: 'Aprobar Pedido',
      message: `¿Aprobar pedido ${pedido.codigo} y enviarlo a preparación?`,
      type: 'warning',
      onConfirm: async () => {
        try {
          await PedidosNuevosAPI.cambiarEstado(pedido.id, 'en_preparacion');
          await cargarPedidos();
          setModalMensaje({
            isOpen: true,
            title: 'Éxito',
            message: 'Pedido aprobado y enviado a cocina',
            type: 'info',
          });
        } catch (err: any) {
          setModalMensaje({
            isOpen: true,
            title: 'Error',
            message: err.message || 'Error al aprobar pedido',
            type: 'danger',
          });
        }
      },
    });
  };

  const handleRechazar = (pedido: PedidoListaItem) => {
    setModalRechazar({
      isOpen: true,
      pedido,
    });
  };

  const handleConfirmarRechazo = async (motivo: string) => {
    if (!modalRechazar.pedido) return;

    try {
      await PedidosNuevosAPI.cambiarEstado(
        modalRechazar.pedido.id,
        'cancelado',
        motivo
      );

      setModalRechazar({ isOpen: false, pedido: null });
      await cargarPedidos();

      setModalMensaje({
        isOpen: true,
        title: 'Pedido Rechazado',
        message: `El pedido ${modalRechazar.pedido.codigo} ha sido rechazado exitosamente`,
        type: 'info',
      });
    } catch (err: any) {
      setModalMensaje({
        isOpen: true,
        title: 'Error',
        message: err.message || 'Error al rechazar pedido',
        type: 'danger',
      });
    }
  };

  const handleCrearNuevo = () => {
    navigate('/armar-pedido');
  };

  const esAdmin = user?.rol === 'administrador';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Pedidos Entrantes
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {totalPedidos} pedido{totalPedidos !== 1 ? 's' : ''} solicitado
            {totalPedidos !== 1 ? 's' : ''} pendiente
            {totalPedidos !== 1 ? 's' : ''} de aprobación
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Botón Nuevo Pedido - Solo para admin */}
          {esAdmin && (
            <button
              onClick={handleCrearNuevo}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Nuevo Pedido</span>
            </button>
          )}

          <div className="flex items-center gap-2 px-3 py-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <Clock className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
            <span className="text-sm font-medium text-yellow-700 dark:text-yellow-300">
              Esperando aprobación
            </span>
          </div>
        </div>
      </div>

      {/* Búsqueda */}
      <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
        <div className="flex gap-2">
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleBuscar()}
            placeholder="Buscar por código, cliente, minuta..."
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          />
          <button
            onClick={handleBuscar}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Search className="w-4 h-4" />
          </button>
          <button
            onClick={cargarPedidos}
            disabled={loading}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Toggle vigentes ↔ vencidos */}
      <ToggleVencidos
        modoVencidos={modoVencidos}
        onCambio={(v) => {
          setPaginaActual(1);
          setModoVencidos(v);
        }}
      />

      {/* Mensajes de estado */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {loading && (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      )}

      {/* Lista de pedidos */}
      {!loading && pedidos.length === 0 && (
        <div className="text-center py-12">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400 text-lg">
            {busqueda
              ? 'No se encontraron pedidos con ese criterio'
              : modoVencidos
                ? 'No hay pedidos vencidos pendientes de aprobación.'
                : '¡Todo al día! No hay pedidos pendientes de aprobación con fecha vigente.'}
          </p>
        </div>
      )}

      {!loading && pedidos.length > 0 && (
        <div className="space-y-4">
          {pedidos.map((pedido) => (
            <TarjetaPedido
              key={pedido.id}
              pedido={pedido}
              onVerDetalle={handleVerDetalle}
              onAccion={(p, accion) => {
                if (accion === 'aprobar') {
                  handleAprobar(p);
                } else if (accion === 'rechazar') {
                  handleRechazar(p);
                }
              }}
              mostrarCliente={true}
              accionesDisponibles={
                esAdmin ? ['aprobar', 'rechazar'] : ['aprobar']
              }
            />
          ))}
        </div>
      )}

      {/* Paginación */}
      {totalPaginas > 1 && (
        <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Página {paginaActual} de {totalPaginas}
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setPaginaActual((p) => Math.max(1, p - 1))}
              disabled={paginaActual === 1}
              className="flex items-center gap-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              <span>Anterior</span>
            </button>

            <button
              onClick={() =>
                setPaginaActual((p) => Math.min(totalPaginas, p + 1))
              }
              disabled={paginaActual === totalPaginas}
              className="flex items-center gap-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <span>Siguiente</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Modal de Confirmación */}
      <ConfirmModal
        isOpen={modalConfirm.isOpen}
        onClose={() => setModalConfirm({ ...modalConfirm, isOpen: false })}
        onConfirm={modalConfirm.onConfirm}
        title={modalConfirm.title}
        message={modalConfirm.message}
        type={modalConfirm.type}
        confirmText="Confirmar"
        cancelText="Cancelar"
      />

      {/* Modal de Rechazo */}
      <RechazarPedidoModal
        isOpen={modalRechazar.isOpen}
        onClose={() => setModalRechazar({ isOpen: false, pedido: null })}
        onConfirm={handleConfirmarRechazo}
        pedidoCodigo={modalRechazar.pedido?.codigo}
      />

      {/* Modal de Mensaje */}
      <ConfirmModal
        isOpen={modalMensaje.isOpen}
        onClose={() => setModalMensaje({ ...modalMensaje, isOpen: false })}
        onConfirm={() => setModalMensaje({ ...modalMensaje, isOpen: false })}
        title={modalMensaje.title}
        message={modalMensaje.message}
        type={modalMensaje.type}
        confirmText="Aceptar"
      />
    </div>
  );
};

export default PedidosEntrantesTab;
